import { type AIAssistantClientSkill, type ViewModelId } from 'balus-common';
import {
  ViewModelAIAssistantSkillRepository,
  type ViewModelAIAssistantSkillMutationResult,
} from './ViewModelAIAssistantSkillRepository';
import { ViewModelLocalSkillStorage } from './ViewModelLocalSkillStorage';

export type { ViewModelAIAssistantSkillMutationErrorCode } from './ViewModelAIAssistantSkillRepository';

const MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS = 20;
const MIGRATION_FAILURE_RETRY_DELAY_MS = 60_000;
const migrationPromises = new Map<ViewModelId, Promise<void>>();
const migrationFailureTimestamps = new Map<ViewModelId, number>();
const migrationFailureCleanupTimers = new Map<ViewModelId, ReturnType<typeof setTimeout>>();

export function mergeViewModelAIAssistantSkills(
  localSkills: AIAssistantClientSkill[],
  rtdbSkills: AIAssistantClientSkill[]
): AIAssistantClientSkill[] {
  const rtdbSkillIds = new Set(rtdbSkills.map((skill) => skill.id));
  return [...rtdbSkills, ...localSkills.filter((skill) => !rtdbSkillIds.has(skill.id))].slice(
    0,
    MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS
  );
}

function getMigrationSourceLocalSkills(viewModelId: ViewModelId): AIAssistantClientSkill[] {
  return ViewModelLocalSkillStorage.getSkills(viewModelId);
}

function removeLocalSkills(viewModelId: ViewModelId): void {
  try {
    ViewModelLocalSkillStorage.removeSkills(viewModelId);
  } catch (error) {
    console.warn('Failed to clear migrated AI assistant skills from localStorage', error);
  }
}

function clearMigrationFailure(viewModelId: ViewModelId): void {
  migrationFailureTimestamps.delete(viewModelId);
  const cleanupTimer = migrationFailureCleanupTimers.get(viewModelId);
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
  }
  migrationFailureCleanupTimers.delete(viewModelId);
}

function rememberMigrationFailure(viewModelId: ViewModelId): void {
  migrationFailureTimestamps.set(viewModelId, Date.now());
  const currentCleanupTimer = migrationFailureCleanupTimers.get(viewModelId);
  if (currentCleanupTimer) {
    clearTimeout(currentCleanupTimer);
  }

  migrationFailureCleanupTimers.set(
    viewModelId,
    setTimeout(() => {
      clearMigrationFailure(viewModelId);
    }, MIGRATION_FAILURE_RETRY_DELAY_MS)
  );
}

function isMigrationRetrySuppressed(viewModelId: ViewModelId): boolean {
  const failedAt = migrationFailureTimestamps.get(viewModelId);
  if (failedAt === undefined) return false;

  if (Date.now() - failedAt < MIGRATION_FAILURE_RETRY_DELAY_MS) return true;

  clearMigrationFailure(viewModelId);
  return false;
}

function migrateLocalSkillsToRTDB(
  viewModelId: ViewModelId,
  localSkills: AIAssistantClientSkill[],
  options?: { retryAfterRecentFailure?: boolean }
): Promise<void> {
  if (localSkills.length === 0) return Promise.resolve();

  const currentMigration = migrationPromises.get(viewModelId);
  if (currentMigration) return currentMigration;
  if (!options?.retryAfterRecentFailure && isMigrationRetrySuppressed(viewModelId)) return Promise.resolve();

  const migration = ViewModelAIAssistantSkillRepository.addMissingSkills(viewModelId, localSkills)
    .then(() => {
      clearMigrationFailure(viewModelId);
      removeLocalSkills(viewModelId);
    })
    .catch((error) => {
      rememberMigrationFailure(viewModelId);
      console.error('Failed to migrate AI assistant skills to RTDB', error);
      throw error;
    })
    .finally(() => {
      migrationPromises.delete(viewModelId);
    });

  migrationPromises.set(viewModelId, migration);
  return migration;
}

export async function getViewModelAIAssistantSkills(viewModelId: ViewModelId): Promise<AIAssistantClientSkill[]> {
  const localSkills = getMigrationSourceLocalSkills(viewModelId);

  try {
    const rtdbSkills = await ViewModelAIAssistantSkillRepository.getSkills(viewModelId);
    const skills = mergeViewModelAIAssistantSkills(localSkills, rtdbSkills);
    try {
      await migrateLocalSkillsToRTDB(viewModelId, localSkills);
    } catch {
      // migrateLocalSkillsToRTDB でログ出力済み。読み取り結果は fallback として返す。
    }
    return skills;
  } catch (error) {
    console.error('Failed to load AI assistant skills from RTDB', error);
    return localSkills;
  }
}

export async function getEnabledViewModelAIAssistantSkills(
  viewModelId: ViewModelId
): Promise<AIAssistantClientSkill[]> {
  return (await getViewModelAIAssistantSkills(viewModelId)).filter((skill) => skill.enabled);
}

export async function saveViewModelAIAssistantSkills(
  viewModelId: ViewModelId,
  skills: AIAssistantClientSkill[]
): Promise<void> {
  await ViewModelAIAssistantSkillRepository.setSkills(viewModelId, skills);
  removeLocalSkills(viewModelId);
}

async function prepareViewModelAIAssistantSkillMutation(viewModelId: ViewModelId): Promise<void> {
  const localSkills = getMigrationSourceLocalSkills(viewModelId);
  if (localSkills.length === 0) return;

  // ユーザー操作時は自動移行のバックオフ中でも再試行する。
  // 移行できないままmutationを続けると、localStorage上のSkillを無視した検査になるため例外は伝播させる。
  await migrateLocalSkillsToRTDB(viewModelId, localSkills, { retryAfterRecentFailure: true });
}

export async function createViewModelAIAssistantSkill(
  viewModelId: ViewModelId,
  skill: AIAssistantClientSkill
): Promise<ViewModelAIAssistantSkillMutationResult> {
  await prepareViewModelAIAssistantSkillMutation(viewModelId);
  return ViewModelAIAssistantSkillRepository.createSkill(viewModelId, skill);
}

export async function updateViewModelAIAssistantSkill(
  viewModelId: ViewModelId,
  expectedSkill: AIAssistantClientSkill,
  skill: AIAssistantClientSkill
): Promise<ViewModelAIAssistantSkillMutationResult> {
  await prepareViewModelAIAssistantSkillMutation(viewModelId);
  return ViewModelAIAssistantSkillRepository.updateSkill(viewModelId, expectedSkill, skill);
}

export async function deleteViewModelAIAssistantSkill(
  viewModelId: ViewModelId,
  expectedSkill: AIAssistantClientSkill
): Promise<ViewModelAIAssistantSkillMutationResult> {
  await prepareViewModelAIAssistantSkillMutation(viewModelId);
  return ViewModelAIAssistantSkillRepository.deleteSkill(viewModelId, expectedSkill);
}

export async function copyViewModelAIAssistantSkills(
  sourceViewModelId: ViewModelId,
  destinationViewModelId: ViewModelId
): Promise<void> {
  const skills = await getViewModelAIAssistantSkills(sourceViewModelId);
  if (skills.length === 0) return;

  await saveViewModelAIAssistantSkills(destinationViewModelId, skills);
}

export function subscribeViewModelAIAssistantSkills(
  viewModelId: ViewModelId,
  onValue: (skills: AIAssistantClientSkill[]) => void,
  onError?: (error: Error) => void
): () => void {
  let hasSubscriptionValue = false;

  return ViewModelAIAssistantSkillRepository.subscribeSkills(
    viewModelId,
    (rtdbSkills) => {
      hasSubscriptionValue = true;
      const localSkills = getMigrationSourceLocalSkills(viewModelId);
      onValue(mergeViewModelAIAssistantSkills(localSkills, rtdbSkills));
      void migrateLocalSkillsToRTDB(viewModelId, localSkills).catch(() => {
        // migrateLocalSkillsToRTDB でログ出力済み。購読は fallback 表示のまま継続する。
      });
    },
    (error) => {
      if (!hasSubscriptionValue) {
        onValue(getMigrationSourceLocalSkills(viewModelId));
      }
      onError?.(error);
    }
  );
}
